import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Upload,
    Loader2,
    Sparkles,
    Zap,
    Users,
    ArrowRight,
    Check,
} from "lucide-react";
import { useSwingPaywall } from "@/lib/swingPaywall";

export default function FrameworkSwing() {
    const [file, setFile] = useState(null);
    const [shot, setShot] = useState("forehand");
    const [focus, setFocus] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const {
        usage,
        payForAi,
        payForHuman,
        paidSessionId,
        refresh,
    } = useSwingPaywall(email, "framework");

    const hasPaidAiPass = Boolean(paidSessionId);

    const submit = async (event) => {
        event.preventDefault();

        if (!hasPaidAiPass) {
            return toast.error(
                "Purchase an AI analysis before uploading your clip."
            );
        }

        if (!file) {
            return toast.error("Upload a short swing clip first.");
        }

        if (!email.trim()) {
            return toast.error("Enter your email first.");
        }

        if (file.size > 40 * 1024 * 1024) {
            return toast.error(
                "File too large. Keep it under 40MB."
            );
        }

        setLoading(true);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append("video", file);
            formData.append("brand", "framework");
            formData.append("shot_type", shot);
            formData.append("focus_area", focus);
            formData.append("name", name);
            formData.append("email", email);
            formData.append("paid_session_id", paidSessionId);

            const response = await api.post(
                "/swing-analysis",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setResult(response.data);
            toast.success("Analysis complete.");
            refresh(email);
        } catch (error) {
            toast.error(
                error?.response?.data?.detail ||
                    "Analysis failed. Try a shorter clip."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="framework-swing-page">
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-12">
                <div className="fw-label mb-4">
                    Swing AI · Beta
                </div>

                <h1 className="fw-display text-5xl sm:text-7xl">
                    Curious what AI thinks about your{" "}
                    <span className="text-[#a6ce39]">
                        swing?
                    </span>
                </h1>

                <div className="mt-7 max-w-3xl space-y-4 text-lg leading-relaxed text-white/65">
                    <p className="font-medium text-white/85">
                        Nobody sees every swing the same way—including
                        coaches.
                    </p>

                    <p>
                        Swing AI is a fun way to get instant feedback,
                        explore ideas, and see what patterns an AI
                        notices. Sometimes it will be spot on.
                        Sometimes it will miss the mark.
                    </p>

                    <p className="font-medium text-[#a6ce39]">
                        That&apos;s part of the fun.
                    </p>

                    <p>
                        AI is constantly improving, but it is not a
                        substitute for a qualified coach. Think of it as
                        another perspective on your game.
                    </p>
                </div>

                <div className="mt-8 max-w-3xl border border-white/10 bg-white/[0.03] p-5 text-sm leading-relaxed text-white/50">
                    <span className="font-semibold text-white/75">
                        Beta Notice:
                    </span>{" "}
                    Swing AI is an experimental feature that is actively
                    improving. Results may vary and should be used
                    alongside—not instead of—professional coaching.
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16">
                <div className="grid gap-5 lg:grid-cols-2">
                    <PricingCard
                        icon={<Zap size={18} />}
                        label="AI Swing Analysis"
                        price={usage.ai_price || 1.99}
                        description="An instant AI-generated breakdown of one short swing clip."
                        features={[
                            "Technical scores",
                            "Strengths and improvement areas",
                            "Suggested drills",
                            "Immediate results",
                        ]}
                        buttonLabel="Try Swing AI"
                        onClick={payForAi}
                        primary
                        testid="fw-swing-pay-ai"
                    />

                    <PricingCard
                        icon={<Users size={18} />}
                        label="Framework Coach Review"
                        price={usage.human_price || 19.99}
                        description="A real Framework coach reviews your swing and provides context the algorithm cannot."
                        features={[
                            "Prioritized improvements",
                            "Personalized recommendations",
                            "Practical next steps",
                            "Reviewed by a real coach",
                        ]}
                        buttonLabel="Choose Coach Review"
                        onClick={payForHuman}
                        testid="fw-swing-pay-human"
                    />
                </div>

                <div className="mt-6 max-w-xl">
                    <label className="block">
                        <div className="fw-label mb-2">
                            Email{" "}
                            <span className="text-[#a6ce39]">
                                required for checkout
                            </span>
                        </div>

                        <input
                            type="email"
                            value={email}
                            onChange={(event) =>
                                setEmail(event.target.value)
                            }
                            placeholder="you@example.com"
                            data-testid="fw-swing-email"
                            className="fw-input"
                        />
                    </label>
                </div>
            </section>

            {hasPaidAiPass && (
                <>
                    <div className="fw-rule max-w-[1400px] mx-auto" />

                    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                        <div className="mb-8">
                            <div className="fw-label mb-3">
                                Paid AI Session Ready
                            </div>

                            <h2 className="fw-display text-3xl sm:text-5xl">
                                Upload your{" "}
                                <span className="text-[#a6ce39]">
                                    clip.
                                </span>
                            </h2>

                            <p className="mt-4 max-w-2xl text-white/55">
                                Upload one 5–20 second swing video. This
                                paid session can be used once.
                            </p>
                        </div>

                        <div className="grid gap-10 lg:grid-cols-2">
                            <form
                                onSubmit={submit}
                                className="border border-white/10 bg-[#0f0f10] p-8 fw-grain"
                            >
                                <div className="fw-label relative z-10 mb-4">
                                    Scanner Input
                                </div>

                                <label
                                    htmlFor="fw-swing-file"
                                    className="relative z-10 mb-6 block cursor-pointer border-2 border-dashed border-white/20 p-8 text-center transition-all hover:border-[#a6ce39]"
                                    data-testid="fw-swing-dropzone"
                                >
                                    <Upload
                                        size={32}
                                        className="mx-auto mb-3 text-[#a6ce39]"
                                        strokeWidth={1.5}
                                    />

                                    <div className="fw-heading text-lg">
                                        {file
                                            ? file.name
                                            : "Choose your clip"}
                                    </div>

                                    <div className="fw-mono mt-2 text-[10px] uppercase tracking-[0.25em] text-white/40">
                                        MP4 / MOV · Up to 40MB · 5–20s
                                        recommended
                                    </div>

                                    <input
                                        id="fw-swing-file"
                                        type="file"
                                        accept="video/*"
                                        onChange={(event) =>
                                            setFile(
                                                event.target.files?.[0] ||
                                                    null
                                            )
                                        }
                                        className="hidden"
                                        data-testid="fw-swing-file-input"
                                    />
                                </label>

                                <div className="relative z-10 mb-6 grid gap-6 sm:grid-cols-2">
                                    <label className="block">
                                        <div className="fw-label mb-2">
                                            Shot Type
                                        </div>

                                        <select
                                            value={shot}
                                            onChange={(event) =>
                                                setShot(event.target.value)
                                            }
                                            data-testid="fw-swing-shot"
                                            className="fw-select"
                                        >
                                            {[
                                                "forehand",
                                                "backhand",
                                                "serve",
                                                "volley",
                                                "return",
                                            ].map((option) => (
                                                <option
                                                    key={option}
                                                    value={option}
                                                >
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="block">
                                        <div className="fw-label mb-2">
                                            Focus Area{" "}
                                            <span className="text-white/35">
                                                optional
                                            </span>
                                        </div>

                                        <input
                                            type="text"
                                            value={focus}
                                            onChange={(event) =>
                                                setFocus(
                                                    event.target.value
                                                )
                                            }
                                            placeholder="e.g. contact point"
                                            data-testid="fw-swing-focus"
                                            className="fw-input"
                                        />
                                    </label>

                                    <label className="block">
                                        <div className="fw-label mb-2">
                                            Name{" "}
                                            <span className="text-white/35">
                                                optional
                                            </span>
                                        </div>

                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(event) =>
                                                setName(event.target.value)
                                            }
                                            data-testid="fw-swing-name"
                                            className="fw-input"
                                        />
                                    </label>

                                    <label className="block">
                                        <div className="fw-label mb-2">
                                            Email
                                        </div>

                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(event) =>
                                                setEmail(
                                                    event.target.value
                                                )
                                            }
                                            required
                                            data-testid="fw-swing-upload-email"
                                            className="fw-input"
                                        />
                                    </label>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !file}
                                    className="fw-btn-primary relative z-10"
                                    data-testid="fw-swing-submit"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2
                                                className="animate-spin"
                                                size={18}
                                            />
                                            Analyzing…
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles
                                                size={18}
                                                strokeWidth={2.5}
                                            />
                                            Run Paid Analysis
                                        </>
                                    )}
                                </button>
                            </form>

                            <div
                                className="min-h-[400px] border border-white/10 bg-[#0a0a0a] p-8"
                                data-testid="fw-swing-result"
                            >
                                <div className="fw-label mb-4">
                                    Scanner Output
                                </div>

                                {!result && !loading && (
                                    <div className="flex h-full items-center justify-center py-16 text-center fw-mono text-xs uppercase tracking-[0.25em] text-white/40">
                                        Awaiting upload…
                                    </div>
                                )}

                                {loading && (
                                    <div className="flex h-full flex-col items-center justify-center gap-3 py-16 text-white/50">
                                        <Loader2
                                            className="animate-spin text-[#a6ce39]"
                                            size={32}
                                        />
                                        <div className="fw-mono text-xs uppercase tracking-[0.25em]">
                                            Reading kinetic chain…
                                        </div>
                                    </div>
                                )}

                                {result && (
                                    <div className="space-y-6">
                                        {Object.keys(
                                            result.scores || {}
                                        ).length > 0 && (
                                            <div>
                                                <div className="fw-label mb-3">
                                                    Scores / 10
                                                </div>

                                                <div className="grid grid-cols-2 gap-3">
                                                    {Object.entries(
                                                        result.scores
                                                    ).map(
                                                        ([key, value]) => (
                                                            <div
                                                                key={key}
                                                                className="border border-white/10 p-3"
                                                            >
                                                                <div className="fw-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                                                    {key.replace(
                                                                        /_/g,
                                                                        " "
                                                                    )}
                                                                </div>

                                                                <div className="fw-heading text-3xl text-[#a6ce39]">
                                                                    {value}
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <div className="fw-label mb-3">
                                                AI Report
                                            </div>

                                            <pre className="whitespace-pre-wrap fw-mono text-xs leading-relaxed text-white/80">
                                                {result.analysis}
                                            </pre>
                                        </div>

                                        <div className="border border-[#a6ce39]/30 bg-[#a6ce39]/5 p-5">
                                            <div className="fw-label mb-2 text-[#a6ce39]">
                                                Want another perspective?
                                            </div>

                                            <h3 className="fw-heading text-xl">
                                                Did the AI get it right?
                                            </h3>

                                            <p className="mt-3 text-sm leading-relaxed text-white/60">
                                                Sometimes yes. Sometimes not
                                                even close. A Framework coach
                                                can review the same swing,
                                                explain what matters most, and
                                                tell you what we would change
                                                first—and why.
                                            </p>

                                            <button
                                                type="button"
                                                onClick={payForHuman}
                                                className="fw-btn-outline mt-5 inline-flex items-center gap-2"
                                                data-testid="fw-swing-post-result-human"
                                            >
                                                Coach Review · $
                                                {usage.human_price ||
                                                    19.99}
                                                <ArrowRight
                                                    size={17}
                                                    strokeWidth={2.5}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}

function PricingCard({
    icon,
    label,
    price,
    description,
    features,
    buttonLabel,
    onClick,
    primary = false,
    testid,
}) {
    return (
        <article
            className={[
                "flex flex-col border p-7 sm:p-8",
                primary
                    ? "border-[#a6ce39]/50 bg-[#a6ce39]/5"
                    : "border-white/15 bg-[#0f0f10]",
            ].join(" ")}
        >
            <div className="flex items-center gap-2 fw-mono text-[10px] uppercase tracking-[0.25em] text-[#a6ce39]">
                {icon}
                {label}
            </div>

            <div className="mt-5">
                <div className="fw-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                    Introductory Beta Pricing
                </div>

                <div className="fw-display mt-1 text-5xl">
                    ${Number(price).toFixed(2)}
                </div>
            </div>

            <p className="mt-5 leading-relaxed text-white/60">
                {description}
            </p>

            <ul className="mt-6 flex-1 space-y-3 text-sm text-white/65">
                {features.map((feature) => (
                    <li
                        key={feature}
                        className="flex items-start gap-2"
                    >
                        <Check
                            size={16}
                            className="mt-0.5 shrink-0 text-[#a6ce39]"
                        />
                        <span>{feature}</span>
                    </li>
                ))}
            </ul>

            <button
                type="button"
                onClick={onClick}
                className={
                    primary
                        ? "fw-btn-primary mt-7"
                        : "fw-btn-outline mt-7"
                }
                data-testid={testid}
            >
                {buttonLabel}
                <ArrowRight size={18} strokeWidth={2.5} />
            </button>
        </article>
    );
}
